import create from 'zustand';
import { ProductVariant } from 'lib/api';
import { CartState, ProductID, SelectedQuantitiesMap, SelectedVariationsMap } from "./interfaces";
import getCartProcessingStrategy from "./getCartProcessingStrategy";
import { DefaultCartProcessingStrategy } from "./strategies/DefaultCartProcessingStrategy";

export const useCartStore = create<CartState>((set) => ({
  loaded: false,
  underAge: false,
  discountsApplied: false,
  cart: null,
  theme: null,
  currency: 'USD',
  countryCode: '+1',
  processor: new DefaultCartProcessingStrategy(),
  selectedQuantities: {},
  selectedVariations: {},
  selectedVariants: {},

  setCart: (cart) => {
    const processor = getCartProcessingStrategy(cart);

    // default to quantity 1 for each item
    const defaultQuantities = cart.items.reduce((out, item) => {
      out[item.id] = 0;
      return out;
    }, {} as SelectedQuantitiesMap);

    // default to first variant of each item
    const defaultVariants = cart.items.reduce((out, item) => {
      out[item.id] = item.variants[0];
      return out;
    }, {} as Record<ProductID, ProductVariant>);

    // default to variations of first variant of each item
    const defaultVariations = Object.keys(defaultVariants).reduce((out, productId, variants) => {
      out[productId] = defaultVariants[productId].variations;
      return out;
    }, {} as SelectedVariationsMap);

    set({
      loaded: true,
      underAge: false,
      discountsApplied: !cart.popup_discounts,
      cart,
      theme: cart.theme ?? 'Neostrata',
      selectedQuantities: defaultQuantities,
      selectedVariants: defaultVariants,
      selectedVariations: defaultVariations,
      currency: cart.shop?.default_currency ?? 'USD',
      countryCode: cart.shop?.default_country_code ?? '+1',
      processor
    });
  },
  selectOption: (productId, selection) =>
    set((state) => {
      const nextSelectedVariations = {
        ...state.selectedVariations,
        [productId]: {
          ...state.selectedVariations[productId],
          ...selection,
        },
      };

      if (state.cart == null) {
        return state;
      }

      const item = state.cart.items.find((i) => i.id === productId);

      if (item == null) {
        return state;
      }

      return {
        ...state,
        selectedVariations: nextSelectedVariations,
        // generate selected variants from selected variations
        selectedVariants: {
          ...state.selectedVariants,
          [productId]: findVariantForVariations(item.variants, nextSelectedVariations[productId]),
        },
      };
    }),
  setUnderAge: (underAge) =>
    set((state) => ({
      ...state,
      underAge,
    })),
  incrementQuantity: (productId) => set((state: CartState) => state.processor.incrementQuantity(productId, state)),
  decrementQuantity: (productId) => set((state: CartState) => state.processor.decrementQuantity(productId, state)),
  onDiscountApplied: () => set(state => state.processor.onDiscountApplied(state)),
  applyDiscounts: () => set(() => ({discountsApplied: true }))
}));

// given the chosen variations, look up the matching variant
function findVariantForVariations(variants: ProductVariant[], selectedVariations: ProductVariant['variations']) {
  const match = variants.find((v) => isEqual(v.variations, selectedVariations));
  if (match == null) {
    throw new Error(`No matching variant for ${JSON.stringify(selectedVariations, null, 2)}`);
  }
  return match;
}

// check if all keys and values match in an object
function isEqual(a: ProductVariant['variations'], b: ProductVariant['variations']) {
  for (let key in b) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  // check for missing keys? (this should never happen)
  for (let key in a) {
    if (!(key in b)) {
      return false;
    }
  }
  return true;
}
